import { useMemo, CSSProperties } from 'react';
import { useVideoConfig } from 'remotion';
import { get, isNumber } from 'radash';
import VideoElementComponent from '../VideoElementComponent/VideoElementComponent';
import { getCropStyles } from '../../../../player-config/src/utils/cropUtils';
import { useRemotionEnvVariables } from '../../MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionEnvVariables';
import { useRemotionUsage } from '../../MainPlayer/RemotionUsageContext/RemotionUsageContext';
import { usePlayerConfigMetadata } from '../PlayerConfigMetadataProvider/PlayerConfigMetadataProvider';
import { VideoCropPositionStyle } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/LayoutChange';
import { VideoCropPosition } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { VideoElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';

function VideoInCompositeSegment({
  video,
  canEditSpeakerLabel,
  videoIndex,
  finalConfig,
  hasScreenshareAtCurrentFrame,
  commonStartFromForElements,
  canManuallyReposition,
  preventOverlappingWithOtherCrops,
  cropInformation,
  muted: mutedInLayout,
  layoutChangeFrame,
  allCropInformation,
  cropStyles,
  animationStyles,
  compositeSegmentId,
  contentId
}: {
  video: VideoElement;
  canEditSpeakerLabel: boolean;
  videoIndex: number;
  finalConfig: any[];
  hasScreenshareAtCurrentFrame: boolean;
  commonStartFromForElements: number;
  canManuallyReposition: boolean;
  cropInformation: VideoCropPosition | null;
  allCropInformation?: Record<string, VideoCropPosition>;
  muted: boolean;
  layoutChangeFrame?: number;
  preventOverlappingWithOtherCrops?: boolean;
  cropStyles?: VideoCropPositionStyle;
  animationStyles?: CSSProperties;
  compositeSegmentId: string;
  contentId: string;
}) {
  const { width: playerWidth, height: playerHeight } = useVideoConfig();

  const { isGeneratingThumbnail } = useRemotionEnvVariables();
  const { previewVolume } = useRemotionUsage();
  const isVideoHidden = videoIndex === -1;

  const { metadata } = usePlayerConfigMetadata();

  // Calculate styles based on video index and configuration
  const newStyle: CSSProperties = isVideoHidden ? {} : (finalConfig[videoIndex]?.style as CSSProperties);
  const newContainerStyle: CSSProperties = useMemo(() => {
    return isVideoHidden
      ? // width, height 0 is to reduce the size of the video element and improve rendering performance
        { zIndex: -1, visibility: 'hidden', width: '1px', height: '1px' }
      : (finalConfig[videoIndex]?.containerStyle as CSSProperties);
  }, [finalConfig, isVideoHidden, videoIndex]);

  const playerAspectRatio = useMemo(() => {
    return playerWidth / playerHeight;
  }, [playerWidth, playerHeight]);

  const computedCropStyles = useMemo(() => {
    if (cropStyles) {
      return cropStyles;
    }
    const allOtherCropInformation = preventOverlappingWithOtherCrops
      ? Object.keys(allCropInformation || {})
          .filter(key => key !== video.id)
          .reduce((acc, key) => {
            acc[key] = allCropInformation?.[key];
            return acc;
          }, {})
      : undefined;
    const finalCropStyles = getCropStyles(
      cropInformation,
      newContainerStyle,
      playerAspectRatio,
      allOtherCropInformation,
      true,
      video.aspectRatio
    );

    return finalCropStyles;
  }, [
    allCropInformation,
    cropInformation,
    cropStyles,
    newContainerStyle,
    playerAspectRatio,
    preventOverlappingWithOtherCrops,
    video.aspectRatio,
    video.id
  ]);

  const { volume, muted } = useMemo(() => {
    if (mutedInLayout) {
      return { volume: {}, muted: true };
    }
    // Prioritize preview volume over metadata volume
    const volumeSource = previewVolume?.[contentId] ?? metadata?.[contentId];
    if (volumeSource) {
      const globalMute = get(volumeSource, `globalControls.muted`, false);
      const streamMute = get(volumeSource, `streamInfo.${video.id}.muted`, false);

      const globalVolume = get(volumeSource, `globalControls.volume`, null);
      const streamVolume = get(volumeSource, `streamInfo.${video.id}.volume`, null);

      if (globalMute || streamMute) {
        return { volume: {}, muted: true };
      }

      // stream volume has higher priority than global
      if (isNumber(streamVolume)) {
        return {
          volume: { volume: [{ value: streamVolume, fromFrame: 0, toFrame: video.toFrame }] },
          muted: false
        };
      }

      if (isNumber(globalVolume)) {
        return {
          volume: { volume: [{ value: globalVolume, fromFrame: 0, toFrame: video.toFrame }] },
          muted: false
        };
      }
    }

    return { volume: {}, muted: mutedInLayout };
  }, [video.id, video.toFrame, previewVolume, contentId, mutedInLayout, metadata]);

  if (isVideoHidden && muted) {
    return null;
  }

  if (isGeneratingThumbnail && isVideoHidden) {
    return null;
  }

  return (
    <>
      <VideoElementComponent
        element={{
          ...video,
          style: {
            ...video.style,
            ...newStyle,
            ...computedCropStyles,
            ...(hasScreenshareAtCurrentFrame && videoIndex === 0 && video.name.includes('screenshare')
              ? { objectFit: 'contain' }
              : {})
          } as CSSProperties,
          containerStyle: {
            ...video.containerStyle,
            ...newContainerStyle
          } as CSSProperties,
          fromFrame: commonStartFromForElements,
          details: {
            ...video.details,
            ...volume,
            ...(muted
              ? {
                  volume: [
                    {
                      value: 0,
                      fromFrame: 0,
                      toFrame: video.toFrame
                    }
                  ]
                }
              : {})
          }
        }}
        disableMediaFragmentHints={true}
        ignoreEndAt
        compositeSegmentId={compositeSegmentId}
        cropInformation={cropInformation || undefined}
        canEditSpeakerLabel={canEditSpeakerLabel}
        canManuallyReposition={canManuallyReposition}
        layoutChangeFrame={layoutChangeFrame}
        animationStylesUserVideo={animationStyles}
      />
    </>
  );
}

export default VideoInCompositeSegment;
